import { ActionIcon, Badge, Code, Flex, Group, Popover, Spoiler, Stack, Text } from '@mantine/core';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import React, { useState } from 'react';
import RerunMenu from '@/components/RerunMenu.tsx';
import { useHover } from '@mantine/hooks';
import CitationsMenu from '@/pages/Run/components/ExtractResults/CitationsMenu.tsx';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { Run } from '@/types/runs.ts';
import { StepRun } from '@/types/stepExecutions.ts';
import { ExtractEntity, ExtractEntityField } from '@/types/extract.ts';
import CachedExecutionIndicator from '@/pages/Run/components/CachedExecutionIndicator.tsx';

type props = {
  run: Run;
  stepRun?: StepRun;
  extractEntity: ExtractEntity;
  extractEntityField: ExtractEntityField;
  isDataViewer: boolean;
};

function renderValue(v: unknown) {
  if (v == null)
    return (
      <Text c="dimmed" fw={400}>
        —
      </Text>
    );

  if (typeof v === 'string') {
    // Detect multiline or paragraph text and render with preserved line breaks
    const hasLineBreaks = v.includes('\n');
    if (hasLineBreaks) {
      return (
        <Text
          fw={400}
          size="sm"
          style={{
            whiteSpace: 'pre-wrap', // preserves \n and wraps long lines
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {v}
        </Text>
      );
    }

    return (
      <Text fw={400} size="sm">
        {v}
      </Text>
    );
  }

  if (typeof v === 'number' || typeof v === 'boolean') {
    return (
      <Text fw={400} size="sm">
        {String(v)}
      </Text>
    );
  }

  // plain object (or anything else): show JSON
  return (
    <Code
      block
      fw={400}
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
      }}
    >
      {JSON.stringify(v as Record<string, unknown>, null, 2)}
    </Code>
  );
}

const ExtractEntityFieldRenderer = ({
  run,
  stepRun,
  extractEntity,
  extractEntityField,
  isDataViewer,
}: props) => {
  const { hovered, ref } = useHover();
  const [isExplanationFocused, setIsExplanationFocused] = useState(false);
  const [isExplanationOpen, setIsExplanationOpen] = useState(false);
  const answerStateLabel =
    extractEntityField.answer_state === 'defaulted'
      ? 'Default'
      : extractEntityField.answer_state === 'unknown_no_default'
        ? 'Unknown'
        : null;

  return (
    <Group align={'center'} gap={'xs'} wrap={'nowrap'} px={'md'} ref={ref} mih={'22px'}>
      {!isDataViewer && <StatusIndicator status={extractEntityField.status} include_text={false} />}
      <Stack gap={'2px'} style={{ flex: 1, minWidth: 0 }}>
        <Group wrap={'nowrap'} gap={'4px'} display={'inline-flex'}>
          <Text fw={600} size={'sm'} component="span">
            {extractEntityField.field_name}
            {extractEntityField.used_existing_extract_entity_field && (
              <span style={{ width: '16px', paddingLeft: '4px' }}>
                <CachedExecutionIndicator label="Value reused from cache" />
              </span>
            )}
          </Text>
          {answerStateLabel && (
            <Badge size="xs" variant="light" color={extractEntityField.answer_state === 'defaulted' ? 'blue' : 'gray'}>
              {answerStateLabel}
            </Badge>
          )}
          {extractEntityField.explanation && (
            <Popover opened={isExplanationOpen} onChange={setIsExplanationOpen} position="top-start" shadow="md" withArrow width={280}>
              <Popover.Target>
                <ActionIcon
                  aria-label="View extraction explanation"
                  aria-expanded={isExplanationOpen}
                  color="gray"
                  variant="subtle"
                  size={16}
                  onClick={() => setIsExplanationOpen((isOpen) => !isOpen)}
                  onFocus={() => setIsExplanationFocused(true)}
                  onBlur={() => setIsExplanationFocused(false)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setIsExplanationOpen(false);
                  }}
                  style={{
                    width: '16px',
                    opacity: hovered || isExplanationFocused || isExplanationOpen ? 1 : 0,
                    transition: 'opacity 150ms ease',
                  }}
                >
                  <HiOutlineInformationCircle size={12} color={'#BABABA'} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="sm">{extractEntityField.explanation}</Text>
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>
        {isDataViewer ? (
          <>{renderValue(extractEntityField.value)} </>
        ) : (
          <Spoiler
            maxHeight={58}
            showLabel="Expand"
            hideLabel="Collapse"
            w="100%"
            styles={{ control: { color: '#868e96', fontSize: '13px', height: '15px' } }}
          >
            {renderValue(extractEntityField.value)}
          </Spoiler>
        )}
      </Stack>

      <Group ml={'auto'} gap={'2px'} wrap={'nowrap'}>
        <Flex w={'25px'}>
          {stepRun && (
            <RerunMenu
              run={run}
              stepRun={stepRun}
              rerunAllData={{
                extractEntitiesToRerun: [
                  {
                    extract_entity_index: extractEntity.index,
                    fields: [extractEntityField.field_id],
                  },
                ],
              }}
              rerunFailedData={null}
              buttonOrIcon={'icon'}
              showOnHover={true}
              hovered={hovered}
            />
          )}
        </Flex>
        {isDataViewer && (
          <CitationsMenu
            dataset_citations={extractEntityField.dataset_citations}
            url_citations={extractEntityField.url_citations}
          />
        )}
      </Group>
    </Group>
  );
};

export default ExtractEntityFieldRenderer;
